import { DataTable } from "@/components/shared/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  formatSalesOrderDate,
  type SalesOrderLine,
  type SalesOrderStatus,
} from "@/mocks/search-sales-orders";

const STATUS_STYLES: Record<SalesOrderStatus, string> = {
  "Pending Fulfillment": "bg-[var(--info-bg)] text-[var(--info)]",
  "Partially Fulfilled": "bg-[var(--warning-bg)] text-[var(--warning)]",
  Backordered: "bg-[var(--danger-bg)] text-[var(--danger)]",
  Closed: "bg-muted text-muted-foreground",
};

interface OrderLineTransferTableProps {
  title: string;
  countText: string;
  rows: SalesOrderLine[];
  emptyMessage: string;
  checkedIds: Set<string>;
  onToggleRow: (lineId: string, checked: boolean) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
  moveLabel: string;
  moveDisabled: boolean;
  onMove: () => void;
  onRowClick: (line: SalesOrderLine) => void;
}

const HEADERS = [
  { label: "" },
  { label: "Order No" },
  { label: "Customer" },
  { label: "Item" },
  { label: "Status" },
  { label: "Location" },
  { label: "Qty" },
  { label: "Expected Ship Date" },
];

export function OrderLineTransferTable({
  title,
  countText,
  rows,
  emptyMessage,
  checkedIds,
  onToggleRow,
  onSelectAll,
  onSelectNone,
  moveLabel,
  moveDisabled,
  onMove,
  onRowClick,
}: OrderLineTransferTableProps) {
  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-semibold">{title}</h3>
          <p className="text-sm text-muted-foreground">{countText}</p>
        </div>
        {rows.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onSelectAll}>
              Select all
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={onSelectNone}>
              Select none
            </Button>
            <Button type="button" size="sm" disabled={moveDisabled} onClick={onMove}>
              {moveLabel}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        <DataTable
          headers={HEADERS}
          empty={<p className="py-8 text-center text-sm text-muted-foreground">{emptyMessage}</p>}
        >
          {rows.map((row) => (
            <tr
              key={row.lineId}
              onClick={() => onRowClick(row)}
              className={cn(
                "cursor-pointer border-b last:border-b-0 hover:bg-muted/40",
                checkedIds.has(row.lineId) && "bg-muted/60",
              )}
            >
              <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  aria-label={`Select ${row.orderNo}`}
                  checked={checkedIds.has(row.lineId)}
                  onChange={(e) => onToggleRow(row.lineId, e.target.checked)}
                  className="size-4 rounded border-input accent-[var(--brand-accent)]"
                />
              </td>
              <td className="px-4 py-3 font-medium">{row.orderNo}</td>
              <td className="px-4 py-3">{row.customer}</td>
              <td className="px-4 py-3">{row.itemCode}</td>
              <td className="px-4 py-3">
                <Badge
                  variant="outline"
                  className={cn("border-transparent", STATUS_STYLES[row.status])}
                >
                  {row.status}
                </Badge>
              </td>
              <td className="px-4 py-3">{row.locationName}</td>
              <td className="px-4 py-3">{row.quantity}</td>
              <td className="px-4 py-3">{formatSalesOrderDate(row.expectedShipDate)}</td>
            </tr>
          ))}
        </DataTable>
      </CardContent>
    </Card>
  );
}
